/**
 * Indian Currency Utilities
 * Handles Indian Rupee formatting and conversions
 */

// Indian currency formatter with ₹ symbol
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format without currency symbol
export const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format in Indian number system (lakhs, crores)
export const formatIndianNumber = (amount: number): string => {
  const num = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  
  if (num >= 10000000) { // 1 crore
    return `${sign}₹${(num / 10000000).toFixed(2)} Cr`;
  } else if (num >= 100000) { // 1 lakh
    return `${sign}₹${(num / 100000).toFixed(2)} L`;
  } else if (num >= 1000) { // 1 thousand
    return `${sign}₹${(num / 1000).toFixed(2)} K`;
  } else {
    return formatCurrency(amount);
  }
};

// Convert number to words in Indian format
const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const convertHundreds = (num: number): string => {
  let result = '';
  
  if (num >= 100) {
    result += ones[Math.floor(num / 100)] + ' Hundred ';
    num %= 100;
  }
  
  if (num >= 20) {
    result += tens[Math.floor(num / 10)] + ' ';
    num %= 10;
  } else if (num >= 10) {
    result += teens[num - 10] + ' ';
    return result;
  }
  
  if (num > 0) {
    result += ones[num] + ' ';
  }
  
  return result;
};

export const numberToWords = (amount: number): string => {
  // Coerce defensively: the API may hand totals over as numeric strings
  // ("1180") or MySQL DECIMALs. A non-finite value must never fall through
  // to the digit logic (that produced "undefined Hundred Rupees Only").
  const value = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(value)) return '';
  if (value === 0) return 'Zero Rupees Only';

  const isNegative = value < 0;
  const abs = Math.abs(value);

  const rupees = Math.floor(abs);
  const paise = Math.round((abs - rupees) * 100);

  let result = '';
  if (isNegative) result += 'Minus ';

  // Indian numbering: decompose into crore / lakh / thousand / hundreds.
  // The previous version was missing the THOUSANDS tier, so every amount
  // from 1,000 to 99,999 hit convertHundreds() with a value >= 1000 and
  // indexed past the `ones` array -> "undefined Hundred".
  let n = rupees;
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000; // 0 - 999 left for convertHundreds

  if (crore > 0) result += convertHundreds(crore) + 'Crore ';
  if (lakh > 0) result += convertHundreds(lakh) + 'Lakh ';
  if (thousand > 0) result += convertHundreds(thousand) + 'Thousand ';
  if (n > 0) result += convertHundreds(n);

  result += 'Rupees';

  if (paise > 0) {
    result += ' and ' + convertHundreds(paise) + 'Paise';
  }

  result += ' Only';

  return result.replace(/\s+/g, ' ').trim();
};

// Parse Indian formatted currency string to number
export const parseCurrency = (currencyString: string): number => {
  // Remove currency symbol and commas, then parse
  const cleanString = currencyString.replace(/[₹,]/g, '');
  return parseFloat(cleanString) || 0;
};

// Validate currency amount
export const isValidAmount = (amount: string | number): boolean => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return !isNaN(num) && num >= 0 && num <= 9999999999.99; // Max 999 crores
};

// Round to 2 decimal places (Indian accounting standard)
export const roundAmount = (amount: number): number => {
  return Math.round(amount * 100) / 100;
};

// Calculate percentage of amount
export const calculatePercentage = (amount: number, percentage: number): number => {
  return roundAmount((amount * percentage) / 100);
};

// Format date in Indian format (DD/MM/YYYY)
export const formatIndianDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

// Format date and time in Indian format
export const formatIndianDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const currencyUtils = {
  formatCurrency,
  formatAmount,
  formatIndianNumber,
  numberToWords,
  parseCurrency,
  isValidAmount,
  roundAmount,
  calculatePercentage,
  formatIndianDate,
  formatIndianDateTime,
};

export default currencyUtils;